import { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Link } from "react-router-dom";
import CustomButton from "../Buttons/CustomButton";
import DeleteConfirmationModal from "../migrations/DeleteConfirmationModal";

function MigrationBreadcrumb({
  setShow,
  setCurrentStep,
  onDelete,
  isDeleteEnabled,
  isDeleting,
  onEditClick,
  isEditEnabled,
  isDeletedView,
  onBulkRestore,
  hasMultipleJobsSelected,
  hasSelectedItems,
  hasSelectedJobs,
  onCancelSelection,
  showCreateButton,
  isPermanentDeleteEnabled,
  onPermanentDelete,
  isDeletingPermanently,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null); // 'delete' or 'restore'
  const getToastStyle = () => ({
    background: "#d4f4d2",
    color: "#000",
    width: "fit-content",
    minWidth: "fit-content",
    textAlign: "left",
    top: "18px",
    borderRadius: "8px",
    fontSize: "14px",
    padding: "12px",
  });
  const ErrorToastStyle = () => ({
    background: "#FFEBEE",
    color: "#D32F2F",
    width: "fit-content",
    minWidth: "fit-content",
    textAlign: "left",
    top: "18px",
    borderRadius: "8px",
    fontSize: "14px",
    padding: "12px",
  });
  const handleActionClick = (action) => {
    if (
      (action === "delete" && isDeleteEnabled) ||
      (action === "restore" && hasSelectedItems) ||
      (action === "delete permanently" && isPermanentDeleteEnabled)
    ) {
      setCurrentAction(action);
      setIsModalOpen(true);
    }
  };

  const handleEditClick = () => {
    if (isEditEnabled) {
      onEditClick();
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleActionConfirm = async () => {
    try {
      if (currentAction === "delete") {
        await onDelete();
        // toast.success("Selected items deleted successfully", {
        //   style: getToastStyle(),
        // });
      } else if (currentAction === "delete permanently") {
        await onPermanentDelete();
        // toast.success("Selected items permanently deleted successfully", {
        //   style: getToastStyle(),
        // });
      } else {
        const success = await onBulkRestore();
        if (success !== false) {
          toast.success(
            "The job has been successfully restored and moved to the job list.",
            {
              style: getToastStyle(),
            }
          );
        }
      }
    } catch (error) {
      toast.error(
        currentAction === "delete"
          ? "Failed to delete selected items"
          : currentAction === "delete permanently"
          ? "Failed to permanently delete selected items"
          : "Failed to restore selected items",
        { style: ErrorToastStyle() }
      );
    } finally {
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center w-100">
        <div>
          <h5 className="fw-bold mb-0">
            {isDeletedView ? "Deleted Code Conversions" : "Code Conversions"}
          </h5>
          <div className="page-title-right">
            <ol className="breadcrumb m-0 p-0">
              <li className="breadcrumb-item font-size-12">
                <Link to="/home">Home</Link>
              </li>
              <li className="breadcrumb-item font-size-12 active ps-0 pe-0">
                <span className="text-dark">
                  {isDeletedView
                    ? "Deleted Code Conversions"
                    : "Run Conversions"}
                </span>
              </li>
            </ol>
          </div>
        </div>
        <div className="d-flex gap-2 ms-auto">
          {isDeletedView ? (
            <>
              <CustomButton
                text="Cancel"
                className="btn btn-edit custom-button"
                isEnabled={hasSelectedItems} // This will disable the button when false
                onClick={onCancelSelection}
                isDisabled={!hasSelectedItems} // Add this to explicitly disable when no items selected
              />
              <CustomButton
                onClick={() => handleActionClick("delete permanently")}
                text="Delete Permanently"
                isDeleteEnabled={isPermanentDeleteEnabled}
                isDisabled={!isPermanentDeleteEnabled}
              />
              <CustomButton
                onClick={() => handleActionClick("restore")}
                text="Restore"
                isEnabled={hasSelectedItems} // Enable if either jobs or files are selected
                isDisabled={!hasSelectedItems}
                className="btn-primary"
              />
            </>
          ) : (
            <>
              <CustomButton
                onClick={handleEditClick}
                text="Edit"
                isEditEnabled={isEditEnabled}
                isDisabled={!isEditEnabled}
              />
              <CustomButton
                onClick={() => handleActionClick("delete")}
                text="Delete"
                isDeleteEnabled={isDeleteEnabled}
                isDisabled={!isDeleteEnabled}
              />
              {showCreateButton && (
                <CustomButton
                  onClick={(e) => {
                    e.preventDefault();
                    setShow(true);
                    setCurrentStep(1);
                  }}
                  text="Start Code Conversion"
                  className="btn btn-primary custom-button"
                />
              )}
            </>
          )}
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleActionConfirm}
        isLoading={
          currentAction === "delete"
            ? isDeleting
            : currentAction === "delete permanently"
            ? isDeletingPermanently
            : false
        }
        message={
          currentAction === "delete"
            ? "Are you sure you want to delete this job or the entire job folder along with its files?"
            : currentAction === "delete permanently"
            ? "Are you sure you want to permanently delete the selected items? This action cannot be undone."
            : `Are you sure you want to restore the selected items? Restoring will reinstate ${
                hasSelectedJobs ? "the jobs" : "the files"
              } and make them available for processing again.`
        }
        confirmText={
          currentAction === "delete"
            ? isDeleting
              ? "Deleting..."
              : "Delete"
            : currentAction === "delete permanently"
            ? isDeletingPermanently
              ? "Deleting..."
              : "Delete Permanently"
            : "Restore"
        }
        confirmClass={
          currentAction === "delete" || currentAction === "delete permanently"
            ? "btn-danger"
            : "btn-primary"
        }
        actionType={currentAction}
      />

      <ToastContainer />
    </>
  );
}

export default MigrationBreadcrumb;
